import { format } from "date-fns";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import useIsMobile from "../../../../hooks/useIsMobile";
import { Skeleton } from "primereact/skeleton";
import { AnimatePresence, motion } from "framer-motion";

const CustomerRemainingPackages = (props) => {
  const { data, error, loading } = props;

  const isMobile = useIsMobile(750);

  const formatPax = (value) => `${value} pax`;

  const formatDate = (dateString) => {
    return format(new Date(dateString), "dd MMM yyyy");
  };

  const TableSkeleton = ({ rows = 10 }) => {
    const skeletonRows = Array.from({ length: rows }, (_, i) => ({ id: i }));

    // Mobile card-style skeleton
    const mobileCardBody = () => (
      <div className="flex flex-col gap-2 w-full">
        <div className="flex flex-row items-center gap-2 w-full">
          <Skeleton shape="square" size="1.5rem" className="flex-shrink-0" />
          <Skeleton height="1rem" width="100%" className="flex-1" />
        </div>
        <Skeleton width="6rem" height="1rem" />
        <Skeleton width="8rem" height="1rem" />
      </div>
    );

    // Table view skeletons
    const nameSkeleton = () => (
      <div className="flex items-center gap-2 w-full">
        {/* Skeleton for the number (like 1., 2., etc.) */}
        <Skeleton
          width="2rem"
          height="2rem"
          className="rounded-md flex-shrink-0"
        />

        {/* Skeleton for the product name */}
        <Skeleton height="1rem" width="100%" />
      </div>
    );

    const quantitySkeleton = () => (
      <div className="flex justify-start">
        <Skeleton width="5rem" height="1rem" />
      </div>
    );

    const totalSkeleton = () => (
      <div className="flex justify-start">
        <Skeleton width="6rem" height="1rem" />
      </div>
    );

    return (
      <div className="w-full rounded-xl overflow-hidden">
        {isMobile ? (
          <DataTable value={skeletonRows} className="w-full" stripedRows>
            <Column
              header="Product Name"
              body={mobileCardBody}
              headerStyle={{ display: "none" }}
            />
          </DataTable>
        ) : (
          <DataTable value={skeletonRows} className="w-full" stripedRows>
            <Column
              header="Name"
              body={nameSkeleton}
              style={{ width: "50%" }} // Make Name column take more space
            />
            <Column
              header="Quantity"
              body={quantitySkeleton}
              style={{ width: "25%" }} // Adjust as needed
            />
            <Column
              header="Total"
              body={totalSkeleton}
              style={{ width: "25%" }} // Adjust as needed
            />
          </DataTable>
        )}
      </div>
    );
  };

  const mobileBodyTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const no = rowIndex + 1;

    return (
      <div className="col-center-start gap-2 w-full">
        {/* Row number */}
        <div className="row-start-start gap-2 w-full">
          <span
            className={`flex items-center justify-center text-sm font-bold text-purple-500`}
          >
            {no}.
          </span>

          {/* Product Name */}

          <span
            className={`w-full font-semibold text-black text-sm`} // Truncate product name
          >
            {rowData.product_name}
          </span>
        </div>

        {/* Quantity */}
        <span className="text-sm text-gray-700 font-semibold ">
          Quantity: {formatPax(rowData.quantity)}
        </span>

        {/* Total Price */}
        <span className="text-sm text-gray-700 font-semibold">
          Exp Date: {formatDate(rowData.expired_date)}
        </span>
      </div>
    );
  };

  const nameBodyTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const no = rowIndex + 1;

    return (
      <div className="row-start-center gap-2">
        <span
          className={`min-w-[3rem] min-h-[3rem] w-[3rem] h-[3rem] flex items-center justify-center text-md font-bold text-purple-500`}
        >
          {no}.
        </span>
        <span className="w-full font-semibold text-sm">
          {rowData.product_name}
        </span>
      </div>
    );
  };

  const dateBodyTemplate = (rowData) => {
    return (
      <div className="font-semibold w-full text-sm">
        {formatDate(rowData.expired_date)}
      </div>
    );
  };

  const quantityBodyTemplate = (rowData) => {
    return (
      <div className="font-semibold w-full text-sm">
        {formatPax(rowData.quantity)}
      </div>
    );
  };

  return (
    <div className="w-full border border-gray-300 rounded-xl overflow-hidden">
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <TableSkeleton rows={10} />
          </motion.div>
        ) : isMobile ? (
          <motion.div
            key="mobile-table"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <DataTable
              value={data?.packages}
              loading={loading}
              className="w-full"
              stripedRows
              selectionMode="single"
              rowClassName={() =>
                "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md w-full"
              }
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
            >
              <Column
                field="products"
                header="Product Name"
                body={mobileBodyTemplate}
                headerStyle={{ display: "none" }}
              />
            </DataTable>
          </motion.div>
        ) : (
          <motion.div
            key="desktop-table"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <DataTable
              value={data?.packages}
              loading={loading}
              className="w-full"
              stripedRows
              selectionMode="single"
              rowClassName={() =>
                "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md w-full"
              }
              paginator
              rows={10}
              removableSort
              rowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
            >
              <Column
                field="product_name"
                header="Product Name"
                body={nameBodyTemplate}
              />
              <Column
                sortable
                field="quantity"
                header="Quantity"
                body={quantityBodyTemplate}
              />
              <Column
                sortable
                field="expired_date"
                header="Expiration Date"
                body={dateBodyTemplate}
              />
            </DataTable>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomerRemainingPackages;
